import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ActionIcon, Box, Button, Divider, Group, Stack, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { IconClock, IconDownload, IconFileText, IconRefresh } from '@tabler/icons-react';
import PartyIdentity from '@/components/app/deals/steps/PartyIdentity';
import UploadZone from '@/components/app/company/UploadZone';
import {
  dealsKeys,
  useAvailableDocTypes,
  useDownloadDocument,
  useUploadSignedForwarderAssignment,
} from '@/hooks/useDeals';
import { MAX_UPLOAD_BYTES, MAX_UPLOAD_MB, validateUploadFiles } from '@/lib/validateUploadFiles';
import type { BargainStepsInfo } from '@/types/deals';


/** Форматы подписанного поручения — те же, что принимает сервер (`bargain_doc_type.doc_format`). */
const SIGNED_ASSIGNMENT_EXTENSIONS = ['pdf', 'docx'];

interface ForwarderAssignmentSigningStepProps {
  readonly dealId: number;
  readonly info: BargainStepsInfo;
}

// Шаг «Поручение экспедитору» (WAIT_AGENT_FORWARDER_ASSIGNMENT_SIGNED). Односторонняя подпись:
// поручение формирует система и кладёт черновиком в ЭДО агента (ООО «ДАТА1861» — отправитель); агент
// подписывает его со своей стороны, подпись экспедитора (получателя) не требуется. Покупатель и
// продавец на этом шаге пассивны — просто ждут перехода к перевозке.
export default function ForwarderAssignmentSigningStep({ dealId, info }: ForwarderAssignmentSigningStepProps) {
  const { t } = useTranslation();
  const myTurn = info.userRole === 'AGENT';
  const forwarder = info.forwarder;

  const docs = (info.documents ?? []).filter(
    (d) => d.documentType === 'SIGNED_FORWARDER_ASSIGNMENT' || d.documentType === 'FORWARDER_ASSIGNMENT_TEMPLATE',
  );

  const download = useDownloadDocument();
  const queryClient = useQueryClient();
  const [checking, setChecking] = useState(false);

  // Загрузка поручения, подписанного вне ЭДО, — резервный путь агента. Доступна ли она, решает сервер: тип
  // документа есть в списке доступных для загрузки только сотруднику агентской организации сделки.
  const availableTypes = useAvailableDocTypes(myTurn ? dealId : null);
  const canUpload = (availableTypes.data ?? []).some((type) => type.documentType === 'SIGNED_FORWARDER_ASSIGNMENT');
  const upload = useUploadSignedForwarderAssignment(dealId);
  const [uploadError, setUploadError] = useState<string | undefined>(undefined);

  async function handleFiles(files: File[]): Promise<void> {
    setUploadError(undefined);
    const result = await validateUploadFiles(files, { extensions: SIGNED_ASSIGNMENT_EXTENSIONS, maxBytes: MAX_UPLOAD_BYTES });
    if (!result.ok) {
      setUploadError(t(result.messageKey, result.params));
      return;
    }
    upload.mutate(result.files[0]!);
  }

  // «Проверить статус»: подтягиваем свежие шаги+документы. Если агент уже подписал в ЭДО и бэкенд это
  // обнаружил (DiadocTrackingJob), шаг сменится — панель перерисуется на «Перевозку» без перезагрузки.
  async function handleCheck(): Promise<void> {
    setChecking(true);
    try {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: dealsKeys.steps(dealId) }),
        queryClient.invalidateQueries({ queryKey: dealsKeys.documents(dealId) }),
      ]);
    } finally {
      setChecking(false);
    }
  }

  // Покупатель/продавец — пассивные наблюдатели: поручение подписывает агент. Экспедитора (ИП+ИНН)
  // показываем и им — чтобы стороны видели, для кого сформировано поручение.
  if (!myTurn) {
    return (
      <Stack gap="md">
        <Group gap="xs" wrap="nowrap">
          <ThemeIcon size={32} radius="xl" variant="light" color="brand">
            <IconClock size={18} />
          </ThemeIcon>
          <Text fz="sm">{t('app.deals.forwarderAssignment.waiting')}</Text>
        </Group>
        <PartyIdentity party={forwarder} />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Text fz="sm" c="gray.8">
        {t('app.deals.forwarderAssignment.edoInstruction')}
      </Text>

      {/* Экспедитор — получатель поручения (ИП + ИНН), чтобы агент нашёл нужный документ в ЭДО. */}
      <PartyIdentity party={forwarder} labelKey="app.deals.forwarderAssignment.forwarderLabel" />


      {/* Документ поручения — просмотр/скачивание (.docx). */}
      {docs.length > 0 ? (
        <Stack gap="xs">
          {docs.map((doc) => (
            <Group key={doc.id} justify="space-between" wrap="nowrap" align="flex-start" gap="sm">
              <Group gap="sm" wrap="nowrap" align="flex-start" style={{ minWidth: 0 }}>
                <ThemeIcon size={32} radius="md" variant="light" color="gray">
                  <IconFileText size={18} />
                </ThemeIcon>
                <Box style={{ minWidth: 0 }}>
                  <Text fz="sm" fw={500} lineClamp={2}>
                    {doc.name ?? t('app.deals.forwarderAssignment.docTitle')}
                  </Text>
                </Box>
              </Group>
              {doc.id != null ? (
                <Tooltip label={t('app.deals.docs.download')}>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    aria-label={t('app.deals.docs.download')}
                    loading={download.isPending && download.variables === doc.id}
                    onClick={() => download.mutate(doc.id!)}
                  >
                    <IconDownload size={18} />
                  </ActionIcon>
                </Tooltip>
              ) : null}
            </Group>
          ))}
        </Stack>
      ) : (
        <Text fz="sm" c="gray.6">
          {t('app.deals.forwarderAssignment.preparing')}
        </Text>
      )}

      <Divider />

      <Text fz="sm" c="gray.8">
        {t('app.deals.forwarderAssignment.afterSign')}
      </Text>

      <Group>
        <Button
          variant="light"
          color="brand"
          leftSection={<IconRefresh size={16} />}
          loading={checking}
          onClick={handleCheck}
        >
          {t('app.deals.forwarderAssignment.checkStatus')}
        </Button>
      </Group>

      {canUpload ? (
        <Box>
          <Text fz="sm" fw={500} mb={4}>
            {t('app.deals.forwarderAssignment.manualTitle')}
          </Text>
          <Text fz="sm" c="gray.7" mb="xs">
            {t('app.deals.forwarderAssignment.manualHint')}
          </Text>
          <UploadZone
            accept={SIGNED_ASSIGNMENT_EXTENSIONS.map((ext) => `.${ext}`).join(',')}
            hint="PDF, DOCX"
            maxSizeMb={MAX_UPLOAD_MB}
            loading={upload.isPending}
            error={uploadError}
            onFiles={handleFiles}
          />
        </Box>
      ) : null}
    </Stack>
  );
}
